import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { auth, db, googleProvider } from "../libs/firebaseConfig";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  sendEmailVerification,
  reload,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

import { useDeviceAccessMonitor } from "../hooks/useDeviceAccessMonitor";
import DeviceAccessWarningModal from "../components/DeviceAccessWarningModal";

import { getFunctions, httpsCallable } from "firebase/functions";


const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // Firebase Auth user
  const [profile, setProfile] = useState(undefined); // undefined = กำลังโหลด, null = ไม่มี doc
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const recaptchaRef = useRef(null);

  const sessionRegisteredRef = useRef(false);
  const sessionInitializingRef = useRef(false);
  const sessionRegistrationRef = useRef({ uid: null, promise: null });

  const registerSingleSessionIfNeeded = async (uid) => {
    const inFlight = sessionRegistrationRef.current;
    if (inFlight.uid === uid && inFlight.promise) {
      return inFlight.promise;
    }

    const registrationPromise = (async () => {
      sessionInitializingRef.current = true;

      try {
        const functions = getFunctions(undefined, "asia-southeast1");
        const callable = httpsCallable(functions, "registerActiveSession");
        const res = await callable();

        const sessionId = res.data?.sessionId;

        if (res.data?.enabled === true && sessionId) {
          localStorage.setItem("activeSessionId", sessionId);
        } else {
          localStorage.removeItem("activeSessionId");
        }

        return res.data;
      } catch (e) {
        console.error("registerActiveSession failed:", e);
        localStorage.removeItem("activeSessionId");
        return null;
      } finally {
        sessionInitializingRef.current = false;
      }
    })();

    sessionRegistrationRef.current = { uid, promise: registrationPromise };

    try {
      return await registrationPromise;
    } finally {
      if (sessionRegistrationRef.current.promise === registrationPromise) {
        sessionRegistrationRef.current = { uid: null, promise: null };
      }
    }
  };

  // --------------------------------------------------------------------------
  // ✅ Sync emailVerified ลง Firestore
  const syncEmailVerified = async (firebaseUser) => {
    if (!firebaseUser) return;
    const ref = doc(db, "users", firebaseUser.uid);
    await setDoc(
      ref,
      {
        emailVerified: !!firebaseUser.emailVerified,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  // --------------------------------------------------------------------------
  // ✅ สร้าง/อัปเดต users/{uid} (ensureUserDoc)
  const ensureUserDoc = async (fbUser, overrides = {}) => {
    const ref = doc(db, "users", fbUser.uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      const baseDoc = {
        uid: fbUser.uid,
        email: fbUser.email ?? "",
        emailVerified: !!fbUser.emailVerified,
        displayName: fbUser.displayName ?? "",
        photoURL: fbUser.photoURL ?? "",
        avatarPath: "",
        bio: "",
        website: "",
        twitter: "",
        facebook: "",
        instagram: "",
        tiktok: "",
        youtube: "",
        linkedin: "",
        // provider: fbUser.providerData?.[0]?.providerId ?? "password",
        providers: fbUser.providerData.map(p => p.providerId),
        primaryProvider: fbUser.providerData?.[0]?.providerId ?? "password",
        phoneNumber: fbUser.phoneNumber ?? "",
        role: "user", // ค่าเริ่มต้น
        status: "active",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await setDoc(ref, { ...baseDoc, ...overrides });
      return { ...baseDoc, ...overrides };
    } else {
      const patch = {
        email: fbUser.email ?? "",
        emailVerified: !!fbUser.emailVerified,
        displayName: fbUser.displayName ?? snap.data().displayName ?? "",
        photoURL: fbUser.photoURL ?? snap.data().photoURL ?? "",
        provider: fbUser.providerData?.[0]?.providerId ?? "password",
        phoneNumber: fbUser.phoneNumber ?? "",
        updatedAt: serverTimestamp(),
      };
      /* const patch = {
        email: fbUser.email ?? "",
        emailVerified: !!fbUser.emailVerified,
        displayName: fbUser.displayName ?? snap.data().displayName ?? "",
        provider: fbUser.providerData?.[0]?.providerId ?? "password",
        phoneNumber: fbUser.phoneNumber ?? "",
        updatedAt: serverTimestamp(),
      }; */
      await setDoc(ref, { ...patch, ...overrides }, { merge: true });
      return { ...snap.data(), ...patch, ...overrides };
    }
  };

  // --------------------------------------------------------------------------
  // ✅ reload user หลัง verify email
  const refreshUser = async () => {
    if (!auth.currentUser) return;
    await reload(auth.currentUser);
    setUser({ ...auth.currentUser });
    await syncEmailVerified(auth.currentUser);
  };

  // --------------------------------------------------------------------------
  // ✅ Observer (Auth + Firestore)
  useEffect(() => {
    let unsubProfile = null;
    let disposed = false;
    let authRunId = 0;

    const unsubAuth = onAuthStateChanged(auth, async (fbUser) => {
      if (disposed) return;
      const currentRunId = ++authRunId;

      setAuthLoading(false);
      setLoading(true);

      // ยกเลิก listener เก่าก่อนเสมอ
      if (unsubProfile) {
        unsubProfile();
        unsubProfile = null;
      }

      try {
        if (!fbUser) {
          sessionRegisteredRef.current = false;
          sessionRegistrationRef.current = { uid: null, promise: null };

          setUser(null);
          setProfile(null);
          setLoading(false);

          try {
            localStorage.removeItem("activeSessionId");
          } catch (error) {
            console.error("Active session cleanup failed:", error);
          }
          return;
        }

        setUser(fbUser);

        // สำคัญ: ระหว่างรอ Firestore profile ให้เป็น undefined
        setProfile(undefined);

        try {
          await ensureUserDoc(fbUser);
        } catch (error) {
          console.error("User profile initialization failed:", error);
          setProfile(null);
        }

        if (disposed || currentRunId !== authRunId) return;

        const sessionData = await registerSingleSessionIfNeeded(fbUser.uid);

        if (disposed || currentRunId !== authRunId) return;

        if (sessionData?.enabled === true && sessionData?.sessionId) {
          sessionRegisteredRef.current = true;
        } else {
          sessionRegisteredRef.current = false;
        }

        const ref = doc(db, "users", fbUser.uid);

        unsubProfile = onSnapshot(
          ref,
          (snap) => {
            if (snap.exists()) {
              const data = snap.data();

              if (sessionInitializingRef.current) {
                setProfile(data);
                setLoading(false);
                return;
              }

              const localSessionId = localStorage.getItem("activeSessionId");

                if (
                  sessionRegisteredRef.current === true &&
                  data?.activeSessionId &&
                  localSessionId &&
                  data.activeSessionId !== localSessionId
                ) {
                  console.warn("[Auth] automatic sign-out", {
                    reason: "single_session_mismatch",
                  });
                  localStorage.removeItem("activeSessionId");
                  sessionRegisteredRef.current = false;

                  signOut(auth);
                  return;
                }

                setProfile(data);
              } else {
                setProfile(null);
              }

            // สำคัญ: ปิด loading หลัง profile โหลดแล้ว
            setLoading(false);
          },
          (error) => {
            console.error("Profile snapshot error:", error);
            setProfile(null);
            setLoading(false);
          }
        );
      } catch (error) {
        console.error("Authenticated state initialization failed:", error);
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      disposed = true;
      authRunId += 1;
      unsubAuth();
      if (unsubProfile) unsubProfile();
    };
  }, []);

  // --------------------------------------------------------------------------
  // ✅ Actions
  const registerEmail = async (email, password, displayName) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);

    if (displayName) await updateProfile(user, { displayName });

    await ensureUserDoc(user, {
      displayName: displayName || user.displayName || "",
      email,
      emailVerified: !!user.emailVerified,
    });

    await sendEmailVerification(user, {
      url: "https://encraft.org", // เปลี่ยนเป็น URL จริงตอน deploy
    });

    return user;
  };

  const loginEmail = async (email, password) => {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    await ensureUserDoc(user);
    return user;
  };

  const loginGoogle = async () => {
    const { user } = await signInWithPopup(auth, googleProvider);
    await ensureUserDoc(user);
    return user;
  };

  // Phone Auth
  const sendOtp = async (phoneNumber, { invisible = true } = {}) => {
    if (!recaptchaRef.current) {
      recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: invisible ? "invisible" : "normal",
      });
    }
    const confirmation = await signInWithPhoneNumber(
      auth,
      phoneNumber,
      recaptchaRef.current
    );
    return confirmation;
  };

  const verifyOtp = async (confirmationResult, code) => {
    const { user } = await confirmationResult.confirm(code);
    await ensureUserDoc(user);
    return user;
  };

  const logout = () => signOut(auth);

  // --------------------------------------------------------------------------
  // ✅ Context value
  const value = useMemo(
    () => ({
      user,
      profile,         // เอกสาร Firestore ของผู้ใช้
      userDoc: profile, // alias (ให้โค้ดอื่นที่ใช้เดิมไม่พัง)
      authLoading,
      loading,
      // actions
      registerEmail,
      loginEmail,
      loginGoogle,
      sendOtp,
      verifyOtp,
      logout,
      // helpers
      refreshUser,
    }),
    [user, profile, authLoading, loading]
  );

  const deviceMonitor = useDeviceAccessMonitor({
    user,
    userDoc: profile,
    loading,
  });

  return (
    <AuthContext.Provider value={value}>
      <div id="recaptcha-container" style={{ display: "none" }} />

      <DeviceAccessWarningModal
        show={deviceMonitor.showWarning}
        result={deviceMonitor.warningResult}
        onConfirm={deviceMonitor.confirmWarning}
      />

      {children}
    </AuthContext.Provider>
  );
}

// --------------------------------------------------------------------------
export const useAuth = () => useContext(AuthContext);
export const useAuthContext = () => useContext(AuthContext);


